import React, { FormEvent, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { AuthHelper, MfaSetupPhoneChallenge, MfaVerifyPhoneChallenge } from '../../helpers/AuthHelper';
import { Spinner } from 'react-bootstrap';

interface SetUserPhoneProps {
  mfaSetupPhoneChallenge: MfaSetupPhoneChallenge;
  onCompleted: (challenge?: MfaVerifyPhoneChallenge) => void;
  onReturn: () => void;
}

export function SetUserPhone({ mfaSetupPhoneChallenge, onCompleted, onReturn }: SetUserPhoneProps): JSX.Element {
  const [phone, setPhone] = useState('+31 6');
  const [submitError, setSubmitError] = useState<string|null>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const phoneWithoutSpaces = phone.replaceAll(' ', '');
  const isValidPhoneNumber = /^\+[0-9]{8,}$/.test(phoneWithoutSpaces);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setSubmitError(null);
    setSubmitLoading(true);
    AuthHelper.handleMfaSetupPhoneChallenge(phone, mfaSetupPhoneChallenge)
      .then(onCompleted)
      .catch(err => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        switch (err.code) {
          case 'AliasExistsException':
            setSubmitError('Dit telefoon nummer is al in gebruik bij een ander account.');
            break;
          case 'TooManyRequestsException':
            setSubmitError('Er zijn te veel pogingen gedaan in een korte periode. Probeer het later opnieuw.');
            break;
          case 'InvalidParameterException':
            setSubmitError('Telefoon nummer is niet geldig, contoleer de invoer.');
            break;
          default:
            setSubmitError('Opslaan telefoonnummer mislukt door een onverwachte fout');
            console.error('Saving phone user attribute failed:', err);
            break;
        }
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const handleReturn = (): void => {
    onReturn();
  };

  const handleInput = (value: string): void => {
    const startsWithPlus = value.trimStart().charAt(0) === '+';
    let clean = value.trimStart().replaceAll(/[^ 0-9]/g, '').replaceAll(/ {2,}/g, ' ');
    if (startsWithPlus) {
      clean = `+${clean}`;
    } else if (clean !== '') {
      clean = `+31 ${clean}`;
    }

    setPhone(clean);
  };

  return <Form className="form" onSubmit={handleSubmit}>
    <Form.Group className="form__item">
      <Form.Label>Mobiel nummer</Form.Label>
      <Form.Control
        type="text"
        autoComplete="tel"
        value={phone}
        onChange={(event) => handleInput(event.target.value)}
      />
      <Form.Text className="text-muted">Vul je mobiele telefoonnummer in inclusief landnummer (<code>+31</code> voor Nederland).</Form.Text>
    </Form.Group>

    { submitError && <Alert variant="danger">{submitError}</Alert> }
    <Button type="submit" className="form__submit" variant="primary" disabled={!isValidPhoneNumber || submitLoading}>
      { submitLoading ? <Spinner variant="primary" animation="border" /> : 'Nummer verifiëren' }
    </Button>
    <Button type="button" className="form__link" variant="link" onClick={handleReturn}>Ga terug</Button>
  </Form>;
}
